import { useEffect, useRef, useState } from "react";
import httpClient from "utils/httpClient";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import { useAuth } from "@frontegg/react";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_EXISTING_ATTACHMENT,
  DELETE_ATTACHMENT,
  GET_ATTACHMENTS,
  UPDATE_ATTACHMENT,
} from "graphql/attachment";
import { toast } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { ImageAsset } from "components/common/receipts/ReceiptReviewDetails";

interface ParentProps {
  parentType: ATTACHMENT_PARENT_TYPE;
  parentId: string;
  subParentId?: string;
  id?: string;
}

interface UseAttachmentAndAssetHandlingOptions {
  parentDetails?: ParentProps;
  onAddAssets?: (asset: LocalImageAsset[]) => void;
  onRemoveAssets?: (assetId: string) => void;
  shouldFetchAttachments?: boolean;
  shouldRefetchAttachments?: boolean;
}

export interface LocalImageAsset extends ImageAsset {
  file?: File;
  isExisting?: boolean;
}

/**
 * What `/documents/scan/invoice` returns: the twelve fields
 * `buildDocumentInvoiceData` builds off Textract, all optional because a scan
 * finds what it finds. Values are the raw strings Textract emits, so a caller
 * that wants a number parses one.
 */
export interface OcrInvoiceResult {
  merchantName?: string;
  merchantPhone?: string;
  merchantAddress?: string;
  merchantUrl?: string;
  paymentTerms?: string;
  dueDate?: string;
  tax?: string;
  amount?: string;
  bankName?: string;
  bankAddress?: string;
  routingNumber?: string;
  accountNumber?: string;
}

export function useAttachmentAndAssetHandling({
  parentDetails,
  shouldFetchAttachments = true,
  shouldRefetchAttachments = true,
  onAddAssets,
  onRemoveAssets,
}: UseAttachmentAndAssetHandlingOptions) {
  const { user } = useAuth();
  const { t } = useTranslation();

  // A count rather than a flag: a second pick can upload while the first still is.
  const [uploadsInFlight, setUploadsInFlight] = useState(0);
  const isSavingAttachments = uploadsInFlight > 0;
  const [uploadingAttachments, setUploadingAttachments] = useState<
    LocalImageAsset[]
  >([]);
  const previewUrls = useRef(new Set<string>());
  const generation = useRef(0);

  const hasParentDetails =
    !!parentDetails?.parentType && !!parentDetails?.parentId;

  // Another record drops this one's previews, and uploads still landing for it change nothing.
  useEffect(() => {
    setUploadingAttachments([]);
    const urls = previewUrls.current;
    return () => {
      generation.current += 1;
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, [parentDetails?.parentId]);

  useEffect(() => {
    const active = new Set(uploadingAttachments.map((asset) => asset.assetUrl));
    previewUrls.current.forEach((url) => {
      if (!active.has(url)) {
        URL.revokeObjectURL(url);
        previewUrls.current.delete(url);
      }
    });
  }, [uploadingAttachments]);

  const generateUniqueId = () => {
    return uuidv4();
  };

  const [updateAttachment] = useMutation(UPDATE_ATTACHMENT);
  const [createExistingAttachment] = useMutation(CREATE_EXISTING_ATTACHMENT);
  const [deleteAttachment, { loading: deletingAttachment }] = useMutation(
    DELETE_ATTACHMENT,
    {
      update: (cache, { data: { deleteAttachment } }) => {
        // this code will remove the attachment from the cache where it likes to hide.
        const normalizedId = cache.identify({
          id: deleteAttachment.id,
          __typename: "Attachment",
        });
        cache.evict({ id: normalizedId });
        cache.gc();
      },
    },
  );

  const {
    data: attachmentData,
    loading: loadingAttachments,
    refetch: refetchQuery,
  } = useQuery(GET_ATTACHMENTS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      parentId: parentDetails?.parentId,
    },
    fetchPolicy: "cache-and-network",
    skip: !hasParentDetails || !shouldFetchAttachments,
    // A refresh after an upload must not join one that left before that file was saved.
    context: { queryDeduplication: false },
  });
  // Apollo sends a refetch even while the query is skipped, so a record not saved yet asks for nothing.
  const refetchAttachments = () =>
    hasParentDetails ? refetchQuery() : Promise.resolve(undefined);

  const attachments =
    attachmentData?.attachments?.edges.map((edge: any) => ({
      id: edge.node.id,
      subParentId: edge.node.subParentId,
      name: edge.node.name,
      description: edge.node.description,
      type: edge.node.type,
      assetUrl: edge.node.fileUrl,
      extension: edge.node.extension,
    })) || [];

  // everything should route here and this should be the only function that is called externally for adding attachments.
  // Resolves false when nothing was added, since failures are toasted here rather than thrown.
  const handleAddAttachments = async (
    attachments: FileList | LocalImageAsset[],
    dynamicParentDetails?: ParentProps,
  ): Promise<boolean> => {
    const details = dynamicParentDetails || parentDetails;
    // Without a saved parent, picked files stay local until the caller saves.
    const parent =
      details?.parentType && details.parentId ? details : undefined;

    if (!attachments || (!parent && !onAddAssets)) {
      console.error("Parent or attachments not provided.");
      return false;
    }

    let attachmentsAdded = false;
    try {
      if (attachments instanceof FileList) {
        if (parent) {
          return await uploadInPlace(attachments, parent);
        } else {
          onAddAssets && addLocalAttachments(attachments, onAddAssets);
        }
      }

      if (attachments instanceof Array && parent) {
        // only upload those that have a file and are not existing (receipts from transactions for example)
        const attachmentsWithFiles = attachments.filter(
          (attachment) => attachment.file && !attachment.isExisting,
        );

        const existingAttachments = attachments.filter(
          (attachment) => attachment.isExisting,
        );

        if (attachmentsWithFiles.length > 0) {
          const attachmentPromises = attachmentsWithFiles.map((attachment) => {
            const file = attachment.file;

            if (file) {
              const fileList = new DataTransfer();
              fileList.items.add(file);

              return uploadAttachments(fileList.files, {
                parentId: parent.parentId,
                parentType: parent.parentType,
                subParentId: attachment?.subParentId || "",
                id: parent.id ? parent.id : undefined,
              });
            } else {
              return Promise.resolve();
            }
          });

          await Promise.all(attachmentPromises);
          attachmentsAdded = true;
        }

        if (existingAttachments.length > 0) {
          const existingAttachmentPromises = existingAttachments.map(
            (attachment) => {
              return createExistingAttachment({
                variables: {
                  input: {
                    fileType: "IMAGE",
                    parentId: parent.parentId,
                    parentType: parent.parentType,
                    subParentId: attachment?.subParentId || "",
                    receiptDetailId: attachment.receiptDetailId,
                    assetId: attachment.id,
                  },
                },
              });
            },
          );

          await Promise.all(existingAttachmentPromises);
          attachmentsAdded = true;
        }
      }
      if (hasParentDetails) {
        shouldRefetchAttachments && refetchAttachments();
        if (attachmentsAdded) {
          toast.success(t("Attachment(s) added successfully"));
        }
      }
      return true;
    } catch (error) {
      console.error("Error adding attachments: ", error);
      return false;
    }
  };

  // add attachments locally (for when a parent is not provided so we can't save to backend)
  // use onAddAssets to handle the local asset
  const addLocalAttachments = (
    files: FileList,
    onAddAssets: (assets: LocalImageAsset[]) => void,
  ) => {
    const newAttachments = Array.from(files).map((file) => {
      const reader = new FileReader();
      return new Promise<LocalImageAsset>((resolve) => {
        reader.onloadend = () => {
          const extension = file.name.split(".").pop() || "";
          resolve({
            id: generateUniqueId(),
            name: file.name,
            description: "",
            type: file.type,
            size: file.size,
            assetUrl: reader.result as string,
            extension: extension,
            file,
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(newAttachments).then((attachments) => {
      onAddAssets(attachments);
    });
  };

  const postAttachment = async (file: File, parent: ParentProps) => {
    const formData = new FormData();
    formData.append("attachment", file);
    formData.append("name", file.name);
    formData.append("description", "");
    formData.append("fileType", "IMAGE");
    parent.subParentId && formData.append("subParentId", parent.subParentId);
    parent.id && formData.append("id", parent.id);

    await httpClient.post(
      `/api/v1/attachments/${parent.parentType}/${parent.parentId}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${user?.accessToken}`,
        },
      },
    );
  };

  // for saving assets to the backend
  const uploadAttachments = async (files: FileList, parent: ParentProps) => {
    setUploadsInFlight((count) => count + 1);

    try {
      for (let i = 0; i < files.length; i++) {
        await postAttachment(files[i], parent);
      }
    } catch (err) {
      toast.error(t("Error adding attachments"));
      throw err;
    } finally {
      setUploadsInFlight((count) => count - 1);
    }
  };

  // Each file shows at once from a local preview and leaves it once its own upload is listed.
  const uploadInPlace = async (
    files: FileList,
    parent: ParentProps,
  ): Promise<boolean> => {
    const uploadGeneration = generation.current;
    const uploads = Array.from(files, (file) => {
      const url = URL.createObjectURL(file);
      previewUrls.current.add(url);
      const preview: LocalImageAsset = {
        id: url,
        assetUrl: url,
        name: file.name,
        extension: file.name.split(".").pop() || "",
        file,
      };
      return { file, preview };
    });
    setUploadingAttachments((current) => [
      ...current,
      ...uploads.map(({ preview }) => preview),
    ]);

    let failed = 0;
    setUploadsInFlight((count) => count + 1);
    try {
      for (const { file, preview } of uploads) {
        let posted = false;
        try {
          await postAttachment(file, parent);
          posted = true;
        } catch (error) {
          failed += 1;
          console.error("Error adding attachment: ", error);
        }
        if (
          posted &&
          generation.current === uploadGeneration &&
          hasParentDetails &&
          shouldRefetchAttachments
        ) {
          // The file is saved either way; a failed refresh leaves it for the next one.
          await refetchAttachments().catch((error) =>
            console.error("Error refreshing attachments: ", error),
          );
        }
        if (generation.current === uploadGeneration) {
          setUploadingAttachments((current) =>
            current.filter((asset) => asset.id !== preview.id),
          );
        }
      }
    } finally {
      setUploadsInFlight((count) => count - 1);
    }

    if (generation.current === uploadGeneration) {
      if (failed > 0) toast.error(t("Error adding attachments"));
      if (failed < uploads.length && hasParentDetails) {
        toast.success(t("Attachment(s) added successfully"));
      }
    }
    return failed === 0;
  };

  /** Resolves false when the delete failed, which it reports itself; true once the list has refreshed. */
  const handleRemoveAttachments = async (
    attachmentId: string,
  ): Promise<boolean> => {
    try {
      toast.dismiss();

      if (hasParentDetails) {
        await deleteAttachment({
          variables: {
            id: attachmentId,
          },
        });
      } else if (onRemoveAssets) {
        onRemoveAssets(attachmentId);
      }

      hasParentDetails && toast.success(t("Attachment removed successfully"));
      if (hasParentDetails && shouldRefetchAttachments) {
        // Awaited, so the caller sees the list without the file; a failed refresh does not undo the delete.
        await refetchAttachments().catch((error) =>
          console.error("Error refreshing attachments: ", error),
        );
      }
      return true;
    } catch (error) {
      toast.error(t("Unable to delete receipt"));
      console.error("Unable to delete receipt.", error);
      return false;
    }
  };

  const handleUpdateAttachments = async (
    frontendAttachments: LocalImageAsset[],
    dynamicParentDetails?: ParentProps,
  ) => {
    const parent = dynamicParentDetails || parentDetails;

    if (!parent?.parentType || !parent?.parentId) {
      console.error(
        "handleUpdateAttachments Error: Parent or attachments not provided.",
      );
      return;
    }

    if (!attachmentData || attachmentData.attachments.length === 0) {
      return;
    }

    const backendAttachmentIds = new Set<string>(
      attachmentData?.attachments?.edges.map((edge: any) => edge.node.id),
    );

    const frontendAttachmentIds = new Set<string>(
      frontendAttachments.map((attachment) => attachment.id),
    );

    // Delete attachments that are in the backend but not in the frontend
    for (const id of backendAttachmentIds) {
      if (!frontendAttachmentIds.has(id)) {
        await deleteAttachment({ variables: { id } });
      }
    }

    // Update attachments that are in both the backend and frontend but have different subParentId values
    // note will only update name, descrtiption, and subparent Id
    for (const attachment of frontendAttachments) {
      if (backendAttachmentIds.has(attachment.id)) {
        const backendAttachment = attachmentData.attachments.edges.find(
          (edge: any) => edge.node.id === attachment.id,
        ).node;

        if (backendAttachment.subParentId !== attachment.subParentId) {
          await updateAttachment({
            variables: {
              id: attachment.id,
              input: {
                subParentId: attachment.subParentId,
                name: attachment.name,
                description: attachment.description,
              },
            },
          });
        }
      }
    }
  };

  ///// OCR ////

  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState<OcrInvoiceResult | null>(null);

  // Generic OCR handler
  const scanFileWithOcr = async (file: File): Promise<OcrInvoiceResult> => {
    setOcrLoading(true);
    setOcrResult(null);

    try {
      const formData = new FormData();
      formData.append("documents", file);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/documents/scan/invoice`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
        },
      );

      if (!response.ok) throw new Error("Failed to scan file");

      const ocrData = await response.json();
      setOcrResult(ocrData);
      return ocrData;
    } catch (error) {
      toast.error(t("Failed to scan file"));
      setOcrResult(null);
      throw error;
    } finally {
      setOcrLoading(false);
    }
  };

  const isLoadingAttachments =
    (loadingAttachments && !attachments) ||
    isSavingAttachments ||
    deletingAttachment;

  const backendFetchingAttachments = loadingAttachments;

  return {
    attachments,
    isLoadingAttachments,
    isUploadingAttachments: isSavingAttachments || deletingAttachment,
    isRemovingAttachment: deletingAttachment,
    uploadingAttachments,
    isFetchingAttachments: loadingAttachments,
    backendFetchingAttachments,
    ocrLoading,
    ocrResult,
    refetchAttachments,
    handleAddAttachments,
    handleRemoveAttachments,
    handleUpdateAttachments,
    scanFileWithOcr,
  };
}
